import { useEffect, useState, useCallback } from 'react'
import {
  apiAdminGetIpWhitelist, apiAdminCreateIpWhitelist, apiAdminDeleteIpWhitelist,
  ApiRequestError, type IpWhitelistEntry, type IpWhitelistAgent, type IpWhitelistInput,
} from '@/lib/api'
import type { OtpConfirmation } from '@nexbit/types'
import { OtpConfirmDialog } from '@/components/OtpConfirmDialog'
import { format } from 'date-fns'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { AlertCircle, Plus, Trash2, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Approved source addresses for the partner API (/api/external/*).
 *
 * The one thing an operator has to understand before using this screen: enforcement is
 * opt-in per agent. An agent with no rows is unrestricted; adding its first row is what
 * starts rejecting every other address for that agent. The banner at the top says so,
 * because getting it wrong locks a live integration out.
 */
export default function AdminIpWhitelistPage() {
  const [entries, setEntries] = useState<IpWhitelistEntry[]>([])
  const [agents, setAgents] = useState<IpWhitelistAgent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [addOpen, setAddOpen] = useState(false)
  const [ipAddress, setIpAddress] = useState('')
  const [partnerId, setPartnerId] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Both mutations here change who can reach the partner API, so both go through a code
  // from the group. The pending value is snapshotted so the code is bound to exactly
  // what gets submitted, not to whatever the form says a minute later.
  const [pendingAdd, setPendingAdd] = useState<IpWhitelistInput | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<IpWhitelistEntry | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiAdminGetIpWhitelist()
      setEntries(data.entries)
      setAgents(data.agents)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  /** Hand the form over to the code dialog; the form's own values stay put in case it is cancelled. */
  function askToAdd() {
    setFormError(null)
    setPendingAdd({
      ipAddress: ipAddress.trim(),
      partnerId: partnerId || null,
      note: note.trim() || null,
    })
    setAddOpen(false)
  }

  async function handleAdd(body: IpWhitelistInput, otp: OtpConfirmation) {
    setSaving(true)
    try {
      await apiAdminCreateIpWhitelist(body, otp)
      setIpAddress(''); setPartnerId(''); setNote('')
      await load()
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(target: IpWhitelistEntry, otp: OtpConfirmation) {
    await apiAdminDeleteIpWhitelist(target.id, otp)
    await load()
  }

  /** Agents that already have at least one entry — i.e. the ones actually being enforced. */
  const enforcedAgentIds = new Set(entries.filter((e) => e.partnerId).map((e) => e.partnerId))

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5 flex items-center justify-between gap-3">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          IP Whitelist — Partner API
        </span>
        <Button size="sm" className="font-mono text-xs h-9 gap-1.5" onClick={() => setAddOpen(true)}>
          <Plus className="h-3.5 w-3.5" /> เพิ่ม IP
        </Button>
      </div>

      <div className="flex items-start gap-2 p-3 border border-border rounded-[var(--radius)] bg-secondary/40 mb-4">
        <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden />
        <p className="font-mono text-[11px] leading-relaxed text-muted-foreground">
          ใช้กับเส้น <span className="text-foreground">/api/external/*</span> ที่ partner เรียกเข้ามา —
          นอกจาก X-Partner-Key แล้ว ต้องมาจาก IP ที่อนุมัติไว้ด้วย
          <br />
          <span className="text-foreground">เอเจนต์ที่ยังไม่มี IP ในรายการ = ไม่ถูกบังคับ</span> (เรียกได้จากทุกที่เหมือนเดิม)
          — พอเพิ่ม IP แรกให้เอเจนต์ไหน เอเจนต์นั้นจะถูกบังคับทันที
          <br />
          แถวที่ไม่เลือกเอเจนต์ = ใช้ได้กับทุกเอเจนต์ที่ถูกบังคับอยู่ (เช่น gateway ที่ใช้ร่วมกัน)
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        {loading ? (
          <div className="p-4 flex flex-col gap-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center">
            <p className="font-mono text-sm text-muted-foreground">ยังไม่มี IP ในรายการ — ทุกเอเจนต์เรียกได้จากทุกที่</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-mono text-xs">IP</TableHead>
                <TableHead className="font-mono text-xs">เอเจนต์</TableHead>
                <TableHead className="font-mono text-xs">หมายเหตุ</TableHead>
                <TableHead className="font-mono text-xs">เพิ่มโดย</TableHead>
                <TableHead className="font-mono text-xs">วันที่</TableHead>
                <TableHead className="font-mono text-xs w-10"> </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="font-mono text-sm font-semibold">{e.ipAddress}</TableCell>
                  <TableCell>
                    {e.partnerId ? (
                      <span className="font-mono text-xs">
                        {e.partnerName}
                        <span className="text-muted-foreground ml-1.5">({e.partnerCode})</span>
                      </span>
                    ) : (
                      <Badge variant="outline" className="font-mono text-[10px]">ทุกเอเจนต์</Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{e.note ?? '—'}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{e.createdByUsername ?? '—'}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {format(new Date(e.createdAt), 'dd MMM yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(e)}
                      aria-label={`ลบ ${e.ipAddress}`}
                      className={cn(
                        'rounded-full p-1.5 text-muted-foreground/70 transition-colors',
                        'hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      )}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* ── Add ─────────────────────────────────────────────────────────────── */}
      <Dialog open={addOpen} onOpenChange={(v) => { if (!v) { setAddOpen(false); setFormError(null) } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm uppercase tracking-wider">เพิ่ม IP</DialogTitle>
          </DialogHeader>

          {formError && (
            <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)]" role="alert">
              <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
              <p className="font-mono text-xs text-destructive">{formError}</p>
            </div>
          )}

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ipAddress" className="font-mono text-xs">IP <span className="text-destructive">*</span></Label>
              <Input
                id="ipAddress"
                className="font-mono text-sm"
                placeholder="203.0.113.9"
                value={ipAddress}
                onChange={(e) => setIpAddress(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="partnerId" className="font-mono text-xs">
                เอเจนต์ <span className="text-muted-foreground">(ไม่เลือกก็ได้ = ทุกเอเจนต์)</span>
              </Label>
              <select
                id="partnerId"
                className={cn(
                  'h-9 rounded-[var(--radius-sm)] border border-input bg-background px-3 font-mono text-sm',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
              >
                <option value="">— ทุกเอเจนต์ —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.code}){enforcedAgentIds.has(a.id) ? '' : ' · ยังไม่ถูกบังคับ'}
                  </option>
                ))}
              </select>
              {partnerId && !enforcedAgentIds.has(partnerId) && (
                <p className="font-mono text-[10px] text-warning">
                  นี่จะเป็น IP แรกของเอเจนต์นี้ — หลังเพิ่ม เอเจนต์นี้จะเรียก API ได้จาก IP ในรายการเท่านั้น
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="note" className="font-mono text-xs">
                หมายเหตุ <span className="text-muted-foreground">(ไม่ใส่ก็ได้)</span>
              </Label>
              <Input
                id="note"
                className="font-mono text-sm"
                placeholder="เช่น server หลักของเอเจนต์"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" size="sm" className="font-mono text-xs" onClick={() => setAddOpen(false)}>
              ยกเลิก
            </Button>
            <Button
              size="sm"
              className="font-mono text-xs"
              disabled={saving || ipAddress.trim().length === 0}
              onClick={askToAdd}
            >
              {saving ? 'กำลังบันทึก...' : 'ถัดไป — ขอรหัสยืนยัน'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {pendingAdd && (
        <OtpConfirmDialog
          purpose="settings.ip-whitelist.add"
          payload={{ body: { ...pendingAdd } }}
          title="ยืนยันการเพิ่ม IP"
          description={`อนุญาต ${pendingAdd.ipAddress} เรียก /api/external/* ` +
            (pendingAdd.partnerId
              ? `สำหรับ ${agents.find((a) => a.id === pendingAdd.partnerId)?.name ?? pendingAdd.partnerId}`
              : 'ในรายการกลาง (ทุกเอเจนต์ที่ถูกบังคับ)')}
          confirmLabel="เพิ่ม IP"
          onConfirm={(otp) => handleAdd(pendingAdd, otp)}
          onDone={() => setPendingAdd(null)}
          onClose={() => { setPendingAdd(null); setAddOpen(true) }}
        />
      )}

      {/* ── Delete: the code dialog IS the confirmation — one step, not two ──── */}
      {confirmDelete && (
        <OtpConfirmDialog
          purpose="settings.ip-whitelist.remove"
          payload={{ params: { id: confirmDelete.id } }}
          title="ยืนยันการลบ IP"
          description={`ลบ ${confirmDelete.ipAddress} ` + (confirmDelete.partnerId
            ? `ออกจากรายการของ ${confirmDelete.partnerName} — ถ้าเป็น IP สุดท้ายของเอเจนต์นี้ เอเจนต์จะกลับไปเรียกได้จากทุกที่`
            : 'ออกจากรายการกลาง — เอเจนต์ที่ถูกบังคับอยู่จะเรียกจาก IP นี้ไม่ได้อีก')}
          confirmLabel="ลบ IP"
          danger
          onConfirm={(otp) => handleDelete(confirmDelete, otp)}
          onDone={() => setConfirmDelete(null)}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}
